/**
 * Ошибки API в формате RFC 9457 Problem Details (`application/problem+json`).
 *
 * Формат общий для всего бэкенда, поэтому разбор живёт в `shared`, а не в фиче: ветвиться
 * по `problemType` приходится и форме создания категории, и экрану входа.
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  errors?: Array<{ field: string; code: string; message: string }>;
}

export class ApiProblemError extends Error {
  /** HTTP-статус ответа. */
  readonly status: number;
  readonly problem: ProblemDetails;
  /**
   * Хвост поля `type` — например `invalid-credentials` из `https://arbuz.by/problems/invalid-credentials`.
   *
   * Пустая строка, если `type` не прислали: по RFC 9457 он не обязателен и по умолчанию
   * равен `about:blank`, а многие стеки так и отвечают на 400 и 409. В этом случае
   * ветвиться нужно по `status`, иначе ошибка уйдёт в общую ветку и поля с `errors[]`
   * потеряются.
   */
  readonly problemType: string;

  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail ?? problem.title ?? `HTTP ${status}`);
    this.name = 'ApiProblemError';
    this.status = status;
    this.problem = problem;
    const type = problem.type?.split('/').at(-1) ?? '';
    this.problemType = type === 'about:blank' ? '' : type;
  }
}

/** Тело ответа, если оно вообще пришло и разобралось: 204 и обрыв прокси дают `null`. */
export async function readJson<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

export async function throwProblem(response: Response): Promise<never> {
  let problem: ProblemDetails = {};
  try {
    problem = (await response.json()) as ProblemDetails;
  } catch {
    // Ответ без тела (упавший прокси, обрыв) — оставляем пустой problem, статус есть.
  }
  throw new ApiProblemError(response.status, problem);
}
